import { useTheme } from 'styled-components';

import { Typography } from '@libs/components';
import { convertExponentialToDecimal, formatBalance } from '@libs/helpers';
import { CurrencySymbol, CurrencyType, RealFiatAssets } from '@libs/types';

import { useTranslation } from 'src/hooks';

import {
  ArrowIcon,
  CustomAssetItem,
  CustomAssetItemDisabledText,
  CustomAssetItemImg,
  CustomAssetItemInfo,
  CustomAssetItemTextCont,
  CustomTableCell,
  Img,
  RightComponentWrapper,
} from './styles';

type CustomProps = {
  testId?: string;
  disabled?: boolean;
  disabledText?: string;
  text: string;
  description?: string;
  infoText?: string;
  icon?: string | JSX.Element;
  onClick?: (event: React.MouseEvent<HTMLButtonElement>) => void;
  amount?: number;
  rate?: string;
  balanceCurrency?: RealFiatAssets;
  isBalanceVisible?: boolean;
};

const CryptoAssetItem: React.FC<CustomProps> = ({
  testId = 'assetItem',
  disabled,
  disabledText = '',
  text,
  description,
  infoText,
  onClick,
  icon,
  amount,
  rate,
  balanceCurrency = CurrencyType.GEL,
  isBalanceVisible,
}) => {
  const { colors } = useTheme();
  const { t } = useTranslation();

  const handleClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    if (disabled || typeof onClick !== 'function') return;
    onClick(e);
  };
  const image =
    typeof icon === 'string' ? <Img src={icon} alt="tableIcon icon" /> : icon;
  const assetAmount =
    convertExponentialToDecimal(amount || 0).toString() + ' ' + description;
  const assetRate = (
    <Typography
      type="caption"
      fontFamily="primaryMedium"
      color={colors.text.disabled}
    >
      {t('common_asset_balance', {
        amount: String(formatBalance(rate)),
        currency: CurrencySymbol[balanceCurrency],
      })}
    </Typography>
  );

  return (
    <CustomAssetItem
      data-testid={testId}
      data-cy={testId}
      disabled={disabled}
      onClick={handleClick}
    >
      {icon && <CustomAssetItemImg>{image}</CustomAssetItemImg>}
      <CustomAssetItemTextCont>
        <Typography
          color={disabled ? colors.text.disabled : colors.text.default}
          fontFamily="primaryMedium"
          type="subHeadline"
        >
          {text}
        </Typography>
        <Typography
          color={disabled ? colors.text.disabled : colors.text.secondary}
          fontFamily="primaryRegular"
          type="caption"
        >
          {description}
        </Typography>
      </CustomAssetItemTextCont>
      {!disabledText && (
        <CustomAssetItemInfo
          color={disabled ? colors.text.disabled : colors.text.secondary}
          fontFamily="primaryMedium"
          type="caption"
        >
          {infoText}
        </CustomAssetItemInfo>
      )}
      {disabledText && (
        <CustomAssetItemDisabledText
          color={colors.text.secondary}
          fontFamily="primaryMedium"
          type="caption"
        >
          {disabledText}
        </CustomAssetItemDisabledText>
      )}
      <RightComponentWrapper>
        {assetAmount && assetRate && isBalanceVisible && (
          <CustomTableCell
            title={assetAmount}
            description={assetRate}
            justifyToLeft={false}
          />
        )}

        <ArrowIcon />
      </RightComponentWrapper>
    </CustomAssetItem>
  );
};
export default CryptoAssetItem;
